import { useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { X, Upload, FileText, FilePdf, FileDoc, CircleNotch } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  renameDocument,
  uploadDocumentVersion,
  type DocFile,
} from '@/lib/documentsApi';
import { getApiErrorMessage } from '@/lib/api';
import { ScrollLock } from '@/components/shared/ScrollLock';

interface Props {
  document: DocFile;
  onClose: () => void;
  /** Called after a successful save so the parent list can refresh. */
  onSaved?: () => void;
}

const ICONS = { pdf: FilePdf, doc: FileDoc, txt: FileText } as const;

export function EditDocumentModal({ document, onClose, onSaved }: Props) {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(document.name);
  const [replacement, setReplacement] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const Icon = ICONS[document.icon] ?? FileText;

  const nameChanged = name.trim() !== '' && name.trim() !== document.name;
  const canSave = (nameChanged || replacement !== null) && !busy;

  const handleSave = async () => {
    if (!canSave) return;
    setBusy(true);
    try {
      // Replace the file first — this creates a new version, preserving history.
      if (replacement) {
        await uploadDocumentVersion(document.id, replacement);
      }
      // Then apply any rename.
      if (nameChanged) {
        await renameDocument(document.id, name.trim());
      }
      qc.invalidateQueries({ queryKey: ['documents'] });
      qc.invalidateQueries({ queryKey: ['documents-search'] });
      qc.invalidateQueries({ queryKey: ['document-versions', document.id] });
      toast.success(
        replacement
          ? 'Document updated — new version uploaded'
          : 'Document renamed',
      );
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not update document'));
    } finally {
      setBusy(false);
    }
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) setReplacement(f);
    e.target.value = '';
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-6"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <ScrollLock />
      <Card className="my-auto flex max-h-[calc(100vh-3rem)] w-full max-w-md flex-col overflow-hidden shadow-pop">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-border px-5 py-3">
          <h2 className="text-sm font-semibold tracking-tight">Edit document</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {/* Name */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Name
            </label>
            <input
              type="text"
              value={name}
              autoFocus
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
              className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
            />
          </div>

          {/* Replace file */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              File
            </label>
            <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/20 p-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-card text-brand-600 dark:text-brand-400">
                <Icon size={18} weight="duotone" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-foreground">
                  {replacement ? replacement.name : document.name}
                </div>
                <div className="text-xs text-muted-foreground">
                  {replacement ? 'New file — replaces current on save' : `Current · ${document.size}`}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="shrink-0 gap-1.5"
                onClick={() => fileInputRef.current?.click()}
                disabled={busy}
              >
                <Upload size={14} />
                {replacement ? 'Change' : 'Replace'}
              </Button>
            </div>
            <p className="mt-1.5 text-[11px] text-muted-foreground">
              Replacing uploads a new version — the previous file is kept in version history.
            </p>
            <input ref={fileInputRef} type="file" className="hidden" onChange={handleFile} />
          </div>
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border px-5 py-3">
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={!canSave} className="gap-1.5">
            {busy && <CircleNotch size={14} className="animate-spin" />}
            Save changes
          </Button>
        </div>
      </Card>
    </div>
  );
}
